import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Paginated } from 'voxloud-types/dist/models';
import { ICallEventSearchFilters, IPhoneCall, IPhoneCallTag, PhoneCallDirections, PhoneCallLogItemEventTypes, PhoneCallTypes } from 'voxloud-types/dist/models/call-history/call-event.model';
import { AppConfig } from '../../../../environments/environment';
import { TranscriptionStatus, UICallHistoryItem } from '../../models/company-call-history/company-call-history.model';
import { AccountFacade } from '../../../account/store/facade';
import { SessionDTO } from '../../../account/store/states-models';
import { map } from 'rxjs/operators';
import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';
import { Observable } from 'rxjs';
import { PaginatedResponse, PaginationModel } from '../../models/pagination.model';

@Injectable({
  providedIn: 'root'
})
export class CompanyCallHistoryService {

  private session: SessionDTO;
  private phoneUtils: PhoneNumberUtil;

  constructor(
    private http: HttpClient,
    private accountFacade: AccountFacade
  ) {
    this.accountFacade.session$.subscribe((s: SessionDTO) => {
      this.session = s;
    });
    this.phoneUtils = PhoneNumberUtil.getInstance();
  }

  public getCallHistoryEvents(filters: ICallEventSearchFilters, size: number, page: number, sort: string = 'timestamp,desc'): Observable<Paginated<IPhoneCall>>{
    const f = {...filters};
    if(f.event_types && f.event_types.includes(PhoneCallTypes.WHATSAPP)) {
      delete f.event_types;
      f.is_whatsapp = true;
    }
    return this.http.post<Paginated<IPhoneCall>>(`${AppConfig.endpointURL}/v1/telephony-events`, {
      ...f,
    }, {
      params: {
        sort: sort,
        size: size,
        page: page
      }
    });
  }

  public getCallHistoryEventsMapped(filters: ICallEventSearchFilters, size: number, page: number, sort: string = 'timestamp,desc') {
    return this.getCallHistoryEvents(filters, size, page, sort).pipe(map((res: Paginated<IPhoneCall>) => {
      const mapped: Paginated<UICallHistoryItem> = {
        ... (<any> res)
      };
      mapped.content = res.content.map((v: IPhoneCall) => this.mapCallHistoryItemToUi(v));
      return mapped;
    }));
  }


  public mapCallHistoryItemToUi(call: IPhoneCall): UICallHistoryItem {
    const answered = this.isAnswered(call);
    return {
      id: call.id,
      type: call.type,
      number: this.getDidLabel(call),
      contactCrm: call.contactCrm,
      contactCrmUrl: call.contactCrmUrl,
      contactCrmIconLink: call.crmLogo,
      from: this.numberParser(call.originatorName),
      to: this.numberParser(call.destinationName),
      fromFull: this.getFullCallee(call, 'originator'),
      toFull: this.getFullCallee(call, 'destination'),
      answerer: this.getAnswerer(call),
      answererFull: this.getFullCallee(call, 'answerer'),
      direction: call.direction,
      duration: answered ? call.duration : this.getDurationFromEntry(call),
      labels: [...call.labels],
      ruleNames: [...call.ruleNames],
      timestamp: call.timestamp,
      recorded: call.recorded,
      recordingUrl: call.recordingUrl || null,
      answered,
      accountId: call.accountId,
      ringTime: call.ringTime,
      maskCallerNumber: call.maskCallerNumber,
      callLinks: {
        from: this.getPhoneLink(call.originatorNumber),
        to: this.getPhoneLink(call.destinationNumber),
        answerer: call.answererNumber ? this.getPhoneLink(call.answererNumber) : ''
      },
      ai: {
        sentimentScore: call.sentiment_score,
        transcription: call.transcription,
        summary: call.summary,
        isProcessing: call.transcription === TranscriptionStatus.PROCESSING, // way to mark call being analyzed
        processingFailed: call.transcription === TranscriptionStatus.FAILED, // mark call that failed processing
        hasData: this.hasAiData(call)
      },
      webhooks: call.webhooks || [],
      voicemail: call.voicemail,
      voicemailUrl: call.voicemailUrl,
      notes: call.notes || '',
      tags: call.tags || [],
      sms: call.sms ?? []
    };
  }

  private hasAiData(call: IPhoneCall): boolean {
    return ![null,undefined].includes(call.summary) &&
      ![null,undefined].includes(call.sentiment_score) &&
      ![null,undefined].includes(call.transcription)
  }

  private getPhoneLink(n: string): string {
    const parsed = this.stripSpaces(this.numberParser(n));
    return parsed;
  }

  private getFullCallee(call:IPhoneCall, direction: string): string {
    const name = call[`${direction}Name`]  ? this.numberParser(call[`${direction}Name`]) : '';
    const num = call[`${direction}Number`] ? this.numberParser(call[`${direction}Number`]) : '';
    return (
      this.stripSpaces(name) === this.stripSpaces(num)
      || call[`${direction}Name`].includes(call[`${direction}Number`])
    )
      ? null
      : `${name} [${num}]`
  }

  private stripSpaces(n:string): string {
    return n.replace(/\s+/g, '' );
  }

  private numberParser(num: string): string {
    // this is looking like extension
    if (this.stripSpaces(num).length <= 3) {
      return num;
    }
    const replacedZeros = num.replace(/^00/, '+');
    const isMasked = num.includes('*');
    try {
      // ????
      if (num) {
        const num = this.phoneUtils.parse('+' + replacedZeros);
        if (this.phoneUtils.isValidNumberForRegion(num)) {
          const formatted = this.phoneUtils.format(num, PhoneNumberFormat.NATIONAL);
          return isMasked ? `${formatted}***` : formatted;
        }

        if (this.phoneUtils.isValidNumber(num)) {
          const formatted = this.phoneUtils.format(num, PhoneNumberFormat.INTERNATIONAL);
          return isMasked ? `${formatted}***` : formatted;
        } else {
          const withPlus = String(replacedZeros).charAt(0) === "+"
            ? this.phoneUtils.parseAndKeepRawInput(replacedZeros)
            : this.phoneUtils.parseAndKeepRawInput("+" + replacedZeros);


          if (this.phoneUtils.isValidNumber(withPlus)) {
            const formatted = this.phoneUtils.format(withPlus, PhoneNumberFormat.INTERNATIONAL);
            return isMasked ? `${formatted}***` : formatted;
          }
        }
        const formatted = this.phoneUtils.format(num, PhoneNumberFormat.INTERNATIONAL);
        return isMasked ? `${formatted}***` : formatted;
      }
    }
    catch (e) {
      return replacedZeros;
    }
  }

  private callingPartyFactory(partyName: string, partyNumber: string) {
    /**
     * partyName name is a string like:
     *      *   "verso *name* da *number*",;
     *      *   "*first_name* *last_name*;
     *
     * so if last item is not a number, we use the whole string, otherwise
     * we use only last item (the number)
     */
    try {
      var lastItemInPartyName = partyName.split(" ").splice(-1)[0];
    }
    catch (e) {
      return null;
    }

    if (this.isNumber(lastItemInPartyName))
      return this.numberParser(lastItemInPartyName);
    else {
      // If it's an extension, it's added to the entry;
      return partyNumber?.length <= 3 ? partyName + ' ' + partyNumber : partyName;
    }
  }

  private isNumber(value: number| Array<any> | string) {
    if (!value || value instanceof Array)
      return false;
    const numberRegex = /^(\+|-)?\d+$/;
    return typeof value === 'number' || numberRegex.test(value);
  };

  private getAnswerer(entry: IPhoneCall): string {
    return this.callingPartyFactory(entry.answererName, entry.answererNumber);
  }

  private getDidLabelFromNumber(number: string) {
    const dids = this.session.company_did_numbers_with_labels;
    const match = dids.find(d => d.number.includes(number));

    return match ? match.label : this.numberParser(number);
  }

  private getDidLabel(entry: IPhoneCall): string {
    if (entry.direction === PhoneCallDirections.INBOUND) {
      return this.getDidLabelFromNumber(entry.destinationNumber);
    } else if (entry.direction === PhoneCallDirections.OUTBOUND) {
      return this.getDidLabelFromNumber(entry.originatorNumber);
    } else {
      return null;
    }
  }

  private getDurationFromEntry(entry: IPhoneCall) {
    if(this.isAnswered(entry)) return;

    if (entry.lastEvent === PhoneCallLogItemEventTypes.CALL_LOST_BUSY_LINES) {
      return 'busy_lines';
    }

    switch(entry.direction){
      case PhoneCallDirections.INBOUND:
        return 'lost';
      case PhoneCallDirections.INTERNAL:
      case PhoneCallDirections.OUTBOUND:
        return 'unanswered';
    }
  }

  private isAnswered(e: IPhoneCall): boolean {
    if (e.duration !== 0) {
      return true;
    }
    switch (e.lastEvent) {
      case PhoneCallLogItemEventTypes.CALL_TERMINATED:
        return true;
      case PhoneCallLogItemEventTypes.CALL_REJECTED:
      case PhoneCallLogItemEventTypes.CALL_LOST_LOSE_RACE:
      case PhoneCallLogItemEventTypes.CALL_LOST_NO_ANSWER:
      case PhoneCallLogItemEventTypes.CALL_LOST_USER_BUSY:
      case PhoneCallLogItemEventTypes.CALL_LOST_BUSY_LINES:
        return false;
    }
    return false;
  }

  public mapPagination(paginatedData: Paginated<UICallHistoryItem>): PaginatedResponse {
    return {
      empty: paginatedData.empty,
      first: paginatedData.first,
      last: paginatedData.last,
      number: paginatedData.number,
      number_of_elements: paginatedData.numberOfElements,
      pageable: {
        offset: paginatedData.pageable.offset,
        page_number: paginatedData.pageable.pageNumber,
        page_size: paginatedData.pageable.pageSize,
        paged: paginatedData.pageable.paged,
        sort: paginatedData.pageable.sort,
        unpaged: paginatedData.pageable.unpaged
      },
      size: paginatedData.size,
      sort: paginatedData.sort,
      total_elements: paginatedData.totalElements || paginatedData.total_elements,
      total_pages: paginatedData.totalPages || paginatedData.total_pages
    }
  }

  public updateCallLogEvent(id: number, notes: string, tags: any): Observable<UICallHistoryItem> {
    return this.http.put<IPhoneCall>(`${AppConfig.endpointURL}/v1/telephony-events/${id}/tags-and-notes`, {
      notes,
      tags
    }, {
      headers: {
        'Voverc-Company-ID': String(this.session.company_id)
      }
    }).pipe(map(res => this.mapCallHistoryItemToUi(res)));
  }

  public getAllTags() {
    return this.http.get<IPhoneCallTag[]>(`${AppConfig.endpointURL}/v1/telephony-events/tags`, {
      headers: {
        'Voverc-Company-ID': String(this.session.company_id)
      }
    });
  }

  public updateTag(tag: IPhoneCallTag): Observable<IPhoneCallTag> {
    return this.http.post<IPhoneCallTag>(`${AppConfig.endpointURL}/v1/telephony-events/tags`, tag, {
      headers: {
        'Voverc-Company-ID': String(this.session.company_id)
      }
    });
  }
}